/**
 * Sync Error Helper
 * Translates technical error messages into user-friendly explanations with actionable suggestions.
 */

// Error patterns and their user-friendly translations
const ERROR_TRANSLATIONS = {
  // BigQuery errors
  'UPDATE/MERGE must match at most one source row': {
    title: 'Duplicate Records Detected',
    message: 'Multiple records with the same key were found in the source data.',
    suggestion: 'Configure deduplication keys in the data source settings or ensure the API returns unique records.',
    type: 'warning'
  },
  'schema mismatch': {
    title: 'Schema Mismatch',
    message: 'The data structure has changed since the last sync.',
    suggestion: 'Click "Sync Now" again to automatically recreate the table with the new schema.',
    type: 'info'
  },
  'Could not convert': {
    title: 'Data Type Conversion Error',
    message: 'Some values could not be converted to the expected data type.',
    suggestion: 'Check the source data for inconsistent value types (e.g., mixing numbers and text in the same field).',
    type: 'error'
  },
  'BIGQUERY_LOAD_FAILED': {
    title: 'BigQuery Load Failed',
    message: 'Failed to load data into the data warehouse.',
    suggestion: 'This is usually temporary. Try syncing again in a few minutes.',
    type: 'error'
  },
  
  // API/Network errors
  'ECONNREFUSED': {
    title: 'Connection Refused',
    message: 'Could not connect to the API endpoint.',
    suggestion: 'Verify the API URL is correct and the server is running.',
    type: 'error'
  },
  'ETIMEDOUT': {
    title: 'Request Timeout',
    message: 'The API took too long to respond.',
    suggestion: 'The server may be under heavy load. Try again later or increase the timeout in settings.',
    type: 'warning'
  },
  'ENOTFOUND': {
    title: 'Server Not Found',
    message: 'The API hostname could not be resolved.',
    suggestion: 'Check the API URL for typos. Ensure the domain name is correct.',
    type: 'error'
  },
  '401': {
    title: 'Authentication Failed',
    message: 'The API rejected your credentials.',
    suggestion: 'Update your API key or authentication credentials in the data source settings.',
    type: 'error'
  },
  '403': {
    title: 'Access Denied',
    message: 'You do not have permission to access this resource.',
    suggestion: 'Check your API permissions or contact the API provider.',
    type: 'error'
  },
  '404': {
    title: 'Resource Not Found',
    message: 'The requested API endpoint does not exist.',
    suggestion: 'Verify the API URL path is correct.',
    type: 'error'
  },
  '429': {
    title: 'Rate Limit Exceeded',
    message: 'Too many requests to the API.',
    suggestion: 'Wait a few minutes before syncing again, or configure a lower request rate.',
    type: 'warning'
  },
  '500': {
    title: 'API Server Error',
    message: 'The API server encountered an internal error.',
    suggestion: 'This is not your fault. Wait and try again later.',
    type: 'error'
  },
  '502': {
    title: 'Bad Gateway',
    message: 'The API server is temporarily unavailable.',
    suggestion: 'Try again in a few minutes.',
    type: 'warning'
  },
  '503': {
    title: 'Service Unavailable',
    message: 'The API server is currently unavailable.',
    suggestion: 'The service may be under maintenance. Try again later.',
    type: 'warning'
  },
  
  // Pagination errors
  'infinite loop': {
    title: 'Pagination Loop Detected',
    message: 'The pagination stopped because it appeared to be in an infinite loop.',
    suggestion: 'Configure the "Total Count Path" or "Has More Path" in pagination settings to properly detect the end of data.',
    type: 'warning'
  },
  'max pages reached': {
    title: 'Maximum Pages Reached',
    message: 'The sync stopped because it reached the maximum page limit.',
    suggestion: 'Increase the "Max Pages" setting or use incremental sync to process data in smaller batches.',
    type: 'info'
  },
  
  // File errors
  'FILE_DATA_EXPIRED': {
    title: 'File Data Expired',
    message: 'The uploaded file data has expired and needs to be re-uploaded.',
    suggestion: 'Click the "Upload New File" button to upload your file again.',
    type: 'warning'
  },
  'expired': {
    title: 'Data Expired',
    message: 'The source data has expired.',
    suggestion: 'Re-upload the file or refresh the data source.',
    type: 'warning'
  },
  
  // Incremental sync errors
  'incremental column not found': {
    title: 'Tracking Column Missing',
    message: 'The incremental sync tracking column was not found in the data.',
    suggestion: 'Check that the tracking column name matches a field returned by the API.',
    type: 'error'
  },
  'invalid date format': {
    title: 'Invalid Date Format',
    message: 'The date/time value could not be parsed.',
    suggestion: 'Check the date format configuration and ensure it matches the API\'s date format.',
    type: 'error'
  }
};

// Patterns that should only match against the error code (not the message body)
// to avoid false positives like "502" matching inside "Processed 5029 rows"
const CODE_ONLY_PATTERNS = new Set([
  '401', '403', '404', '429', '500', '502', '503'
]);

/**
 * Translate a sync error into a user-friendly format
 * @param {Object|string} error - The error object or message
 * @returns {Object} - Translated error with title, message, suggestion, and type
 */
export function translateSyncError(error) {
  const errorMessage = typeof error === 'string' ? error : (error?.message || error?.error || '');
  const errorCode = error?.code || '';
  const statusCode = error?.status || error?.statusCode || '';
  const errorString = `${errorCode} ${errorMessage}`.toLowerCase();
  
  // Search for matching patterns
  for (const [pattern, translation] of Object.entries(ERROR_TRANSLATIONS)) {
    if (CODE_ONLY_PATTERNS.has(pattern)) {
      // For HTTP status codes, only match against code/status fields (not message text)
      if (String(errorCode) === pattern || String(statusCode) === pattern) {
        return {
          ...translation,
          originalMessage: errorMessage,
          originalCode: errorCode
        };
      }
    } else if (errorString.includes(pattern.toLowerCase())) {
      return {
        ...translation,
        originalMessage: errorMessage,
        originalCode: errorCode
      };
    }
  }
  
  // Default fallback
  return {
    title: 'Sync Error',
    message: errorMessage || 'An unknown error occurred during sync.',
    suggestion: 'Check the server logs for more details or contact support if the issue persists.',
    type: 'error',
    originalMessage: errorMessage,
    originalCode: errorCode
  };
}

/**
 * Get CSS classes for error type
 * @param {string} type - The error type (error, warning, info)
 * @returns {Object} - CSS class names for styling
 */
export function getErrorStyles(type) {
  switch (type) {
    case 'warning':
      return {
        bg: 'bg-amber-50 dark:bg-amber-900/20',
        border: 'border-amber-200 dark:border-amber-800',
        icon: 'text-amber-500',
        title: 'text-amber-800 dark:text-amber-200',
        text: 'text-amber-700 dark:text-amber-300'
      };
    case 'info':
      return {
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        border: 'border-blue-200 dark:border-blue-800',
        icon: 'text-blue-500',
        title: 'text-blue-800 dark:text-blue-200',
        text: 'text-blue-700 dark:text-blue-300'
      };
    case 'error':
    default:
      return {
        bg: 'bg-red-50 dark:bg-red-900/20',
        border: 'border-red-200 dark:border-red-800',
        icon: 'text-red-500',
        title: 'text-red-800 dark:text-red-200',
        text: 'text-red-700 dark:text-red-300'
      };
  }
}

const syncErrorHelper = {
  translateSyncError,
  getErrorStyles
};

export default syncErrorHelper;
